import React, { useEffect, useRef } from 'react';
// Use default imports -- webpack resolves the UMD wrappers correctly via import
import Highcharts from 'highcharts';
import 'highcharts/highcharts-more';
import 'highcharts/modules/heatmap';
import { useTheme } from 'src/components/context/ThemeContextProvider';

interface HighchartsChartProps {
  options: Highcharts.Options;
  height?: number | string;
}

const CHART_FONT_FAMILY = "Inter, 'Ember Modern Text', 'Helvetica Neue', Roboto, Arial, sans-serif";

/**
 * Thin React wrapper around Highcharts (vendored, no highcharts-react-official
 * dependency — mirrors the kingpin AgChart.tsx pattern).
 *
 * Theme-aware: consumes the global `isDarkMode` flag from ThemeContextProvider
 * (the same flag AG Grid / AG Charts read) and applies a matching transparent
 * theme so flipping the top-nav toggle re-themes every chart in one click.
 *
 * The container background is left transparent so the surrounding Cloudscape
 * Container (and its glassmorphism) shows through.
 */
const buildBaseTheme = (isDark: boolean): Highcharts.Options => {
  const text = isDark ? '#e8ecff' : '#1a2236';
  const muted = isDark ? '#8a93b8' : '#64708a';
  const gridLine = isDark ? 'rgba(120,140,200,0.16)' : 'rgba(40,60,110,0.10)';
  return {
    chart: {
      backgroundColor: 'transparent',
      style: { fontFamily: CHART_FONT_FAMILY },
      spacing: [12, 8, 8, 8]
    },
    title: { text: undefined },
    credits: { enabled: false },
    legend: {
      itemStyle: { color: text, fontWeight: '500', fontSize: '12px' },
      itemHoverStyle: { color: muted }
    },
    xAxis: {
      labels: { style: { color: muted, fontSize: '11px' } },
      lineColor: gridLine,
      tickColor: gridLine,
      gridLineColor: gridLine,
      title: { style: { color: muted } }
    },
    yAxis: {
      labels: { style: { color: muted, fontSize: '11px' } },
      gridLineColor: gridLine,
      title: { style: { color: muted } }
    },
    tooltip: {
      backgroundColor: isDark ? '#121a36' : '#ffffff',
      borderColor: gridLine,
      borderRadius: 10,
      style: { color: text, fontSize: '12px' },
      shadow: true
    }
  };
};

export const HighchartsChart: React.FC<HighchartsChartProps> = ({ options, height = 300 }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<Highcharts.Chart | null>(null);
  const { isDarkMode } = useTheme();

  useEffect(() => {
    if (!containerRef.current) return undefined;
    const merged = Highcharts.merge(buildBaseTheme(isDarkMode), options, {
      chart: { height: typeof height === 'number' ? height : undefined }
    });
    chartRef.current = Highcharts.chart(containerRef.current, merged);
    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
    // Re-create on options or theme change for a clean re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, isDarkMode, height]);

  return <div ref={containerRef} style={{ width: '100%', height }} />;
};
